import { VariantProps } from 'class-variance-authority';
import {
  DateField as AriaDateField,
  DateFieldProps as AriaDateFieldProps,
  DateInput as AriaDateInput,
  DateInputProps as AriaDateInputProps,
  DateSegment as AriaDateSegment,
  DateSegmentProps as AriaDateSegmentProps,
  DateValue as AriaDateValue,
  TimeField as AriaTimeField,
  TimeFieldProps as AriaTimeFieldProps,
  TimeValue as AriaTimeValue,
  ValidationResult as AriaValidationResult,
  composeRenderProps,
  Text,
} from 'react-aria-components';

import { cn } from '../../../lib/stylingUtils';
import { FieldError, fieldGroupVariants, Label } from '../Field';

const DateSegment = ({ className, ...props }: AriaDateSegmentProps) => (
  <AriaDateSegment
    className={composeRenderProps(className, (className) =>
      cn(
        '@DateSegment',
        'inline rounded p-[1px] caret-transparent outline-0',
        /* Placeholder */
        'data-[placeholder]:text-foreground/20',
        /* Disabled */
        'rac-disabled:cursor-not-allowed rac-disabled:opacity-50',
        /* Focused */
        'rac-focus:bg-primary rac-focus:text-white',
        /* Invalid */
        'rac-invalid:text-failure rac-invalid:data-[placeholder]:text-failure/20 rac-invalid:rac-focus:bg-failure/20 rac-invalid:rac-focus:text-neutral rac-invalid:rac-focus:data-[placeholder]:text-white',
        className,
      ),
    )}
    {...props}
  />
);

interface DateInputProps
  extends AriaDateInputProps, VariantProps<typeof fieldGroupVariants> {}

const DateInput = ({ className, children, ...props }: DateInputProps) => (
  <AriaDateInput
    className={composeRenderProps(className, (className) =>
      cn('@DateInput', 'text-sm', className),
    )}
    {...props}
  >
    {children}
  </AriaDateInput>
);

interface DateFieldProps<
  T extends AriaDateValue,
> extends AriaDateFieldProps<T> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: AriaValidationResult) => string);
}

const DateField = <T extends AriaDateValue>({
  label,
  description,
  className,
  errorMessage,
  ...props
}: DateFieldProps<T>) => (
  <AriaDateField
    className={composeRenderProps(className, (className) =>
      cn('@DateField', 'group flex flex-col gap-2', className),
    )}
    {...props}
  >
    <Label aria-required={props.isRequired}>{label}</Label>
    <DateInput>{(segment) => <DateSegment segment={segment} />}</DateInput>
    {description && (
      <Text className="text-sm text-neutral/20" slot="description">
        {description}
      </Text>
    )}
    <FieldError>{errorMessage}</FieldError>
  </AriaDateField>
);

interface TimeFieldProps<
  T extends AriaTimeValue,
> extends AriaTimeFieldProps<T> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: AriaValidationResult) => string);
}

const TimeField = <T extends AriaTimeValue>({
  label,
  description,
  errorMessage,
  className,
  ...props
}: TimeFieldProps<T>) => (
  <AriaTimeField
    className={composeRenderProps(className, (className) =>
      cn('@TimeField', 'group flex flex-col gap-2', className),
    )}
    {...props}
  >
    <Label>{label}</Label>
    <DateInput>{(segment) => <DateSegment segment={segment} />}</DateInput>
    {description && (
      <Text className="text-sm text-neutral/20" slot="description">
        {description}
      </Text>
    )}
    <FieldError>{errorMessage}</FieldError>
  </AriaTimeField>
);

export { DateSegment, DateInput, DateField, TimeField };
export type {
  AriaDateValue as DateValue,
  DateInputProps,
  DateFieldProps,
  TimeFieldProps,
};
